import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Combobox API",
  "title": "Combobox",
  "props": ["ComboboxProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const Alert = makeShortcode("Alert");
const ComboboxProps = makeShortcode("ComboboxProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="combobox" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  A Combobox is similar to a Select component, in that it is used for collecting
  user provided information from a list of options. However, a combobox also
  allows users to type in the field, either to create their own option or to see
  typeahead suggestions.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`For simple Select functionality, please see the `}<Link to="/api/select" mdxType="Link">{`Select component`}</Link>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      id="comboboxId"
      labelText="Combobox"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      placeholder="Hello"
    />
  );
}
`}</code></pre>
      <h2>{`Initial State`}</h2>
      <p>{`Set initial state using the `}<inlineCode parentName="p">{`initialSelectedItem`}</inlineCode>{`, `}<inlineCode parentName="p">{`initialSelectedItems`}</inlineCode>{`, and `}<inlineCode parentName="p">{`initialHighlightedIndex`}</inlineCode>{` props.`}</p>
      <Alert variant="info" mdxType="Alert">
  When using any of the <inlineCode className="inverse">initial*</inlineCode>{' '}
  props be aware that passing in the controlled version of that prop will
  overwrite the initial version.
  <br />
  <br />
  <strong>Ex: </strong>
  <inlineCode className="inverse">selectedItem</inlineCode> takes precedence
  over <inlineCode className="inverse">initialSelectedItem</inlineCode>
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Combobox
        id="initialSelectedByItemSelectId"
        labelText="Initial selected by item"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialSelectedItem={{ label: 'Green', value: 'green' }}
      />

      <Combobox
        id="initialHighlightedIndexSelectId"
        labelText="Initial highlighted index"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialHighlightedIndex={1}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Specific State Changes`}</h2>
      <p>{`You can track changes made to the internal state of the `}<inlineCode parentName="p">{`select`}</inlineCode>{` using the following functions: `}<inlineCode parentName="p">{`onHighlightedIndexChange`}</inlineCode>{`,
`}<inlineCode parentName="p">{`onIsOpenChange`}</inlineCode>{`, `}<inlineCode parentName="p">{`onSelectedItemChange`}</inlineCode>{`, and `}<inlineCode parentName="p">{`onStateChange`}</inlineCode>{`. Each of these functions will have a `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object as a parameter that
includes the changes made to state from each action as well as a `}<inlineCode parentName="p">{`type`}</inlineCode>{` that describes the action.`}</p>
      <p><inlineCode parentName="p">{`onStateChange`}</inlineCode>{` is best used when using the `}<inlineCode parentName="p">{`select`}</inlineCode>{` in a controlled state because it is called on every state change.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const [highlightedIndexChangeCount, updateHighlightedIndexChangeCount] =
    React.useState<number>(0);
  const [isOpenChangeCount, updateIsOpenChangeCount] =
    React.useState<number>(0);
  const [selectedItem, updateSelectedItem] = React.useState(undefined);

  function onHighlightedIndexChange(changes) {
    updateHighlightedIndexChangeCount(highlightedIndexChangeCount + 1);
  }

  function onIsOpenChange(changes) {
    updateIsOpenChangeCount(isOpenChangeCount + 1);
  }

  function onSelectedItemChange(changes) {
    updateSelectedItem(changes.selectedItem);
  }

  return (
    <>
      <strong>Selected Item: </strong>
      <pre>{JSON.stringify(selectedItem, null, 2)}</pre>
      <p>
        <strong>Highlighted Index Change Count: </strong>
        {highlightedIndexChangeCount}
      </p>
      <p>
        <strong>Is Open Change Count: </strong>
        {isOpenChangeCount}
      </p>

      <Combobox
        id="individualStateChangesId"
        labelText="Individual state changes"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        onHighlightedIndexChange={onHighlightedIndexChange}
        onIsOpenChange={onIsOpenChange}
        onSelectedItemChange={onSelectedItemChange}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Generic State Change`}</h2>
      <Alert variant="warning" mdxType="Alert">
  Using <inlineCode>onStateChange</inlineCode> and controlling every piece of{' '}
  <inlineCode>state</inlineCode> will result in losing most of the functionality
  that has been built in to this component. Only use this feature as a last
  resort.
      </Alert>
      <p><inlineCode parentName="p">{`onStateChange`}</inlineCode>{` is called when any internal state is changed. Therefore, it is best used when using the
`}<inlineCode parentName="p">{`combobox`}</inlineCode>{` in a controlled state.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`onStateChange`}</inlineCode>{` function passes a `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object that includes properties that have changed since the
last state change. The `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object also includes a `}<inlineCode parentName="p">{`type`}</inlineCode>{` property that describes the action taken
to change the state. You can see the full list of the types in the `}<a parentName="p" {...{
          "href": "/version/4.9.1/api/combobox/#state_change_types"
        }}><inlineCode parentName="a">{`stateChangeTypes`}</inlineCode></a>{` section.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox, ComboboxStateChangeTypes } from 'react-magma-dom';

export function Example() {
  const [changes, updateChanges] = React.useState({});
  const [highlightedIndex, updateHighlightedIndex] = React.useState<number>(-1);
  const [isOpen, updateIsOpen] = React.useState<boolean>(false);
  const [selectedItem] = React.useState({});

  function onStateChange(newChanges) {
    const { type } = newChanges;

    updateChanges(newChanges);

    switch (type) {
      case ComboboxStateChangeTypes.ToggleButtonClick:
        updateIsOpen(!isOpen);
        break;
      case ComboboxStateChangeTypes.InputKeyDownArrowDown:
        if (newChanges.isOpen) {
          updateIsOpen(newChanges.isOpen);
        }
        updateHighlightedIndex(newChanges.highlightedIndex);
        break;
      case ComboboxStateChangeTypes.ItemMouseMove:
      case ComboboxStateChangeTypes.FunctionSetHighlightedIndex:
      case ComboboxStateChangeTypes.InputKeyDownArrowUp:
        updateHighlightedIndex(newChanges.highlightedIndex);
        break;
      case ComboboxStateChangeTypes.InputKeyDownEscape:
      case ComboboxStateChangeTypes.InputBlur:
        updateIsOpen(false);
        updateHighlightedIndex(newChanges.highlightedIndex);
        break;
      case ComboboxStateChangeTypes.ItemClick:
      case ComboboxStateChangeTypes.InputKeyDownEnter:
        updateHighlightedIndex(newChanges.highlightedIndex);
        updateIsOpen(newChanges.isOpen);
        updateSelectedItem(newChanges.selectedItem);
        break;
      default:
        break;
    }
  }

  return (
    <>
      <pre>{JSON.stringify(changes, null, 2)}</pre>
      <strong>Is Open: </strong>
      {isOpen.toString()}

      <Combobox
        id="stateChangesId"
        labelText="State changes"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        onStateChange={onStateChange}
        highlightedIndex={highlightedIndex}
        isOpen={isOpen}
        selectedItem={selectedItem}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Controlled Items`}</h2>
      <p>{`If you would like to control your `}<inlineCode parentName="p">{`items`}</inlineCode>{` and any updates to them on creation you can pass in a function to the `}<inlineCode parentName="p">{`onItemCreated`}</inlineCode>{`
prop. If no `}<inlineCode parentName="p">{`newItemTransform`}</inlineCode>{` function is passed in, the created item will be the string value of what was created.`}</p>
      <p><strong parentName="p">{`NOTE`}</strong>{`: Your created items will not be added to the `}<inlineCode parentName="p">{`items`}</inlineCode>{` list internally. You will have to pass the updated `}<inlineCode parentName="p">{`items`}</inlineCode>{` array to the `}<inlineCode parentName="p">{`items`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const defaultItems = [
    { label: 'Red', value: 'red' },
    { label: 'Blue', value: 'blue' },
    { label: 'Green', value: 'green' },
  ];
  const [items, updateItems] = React.useState(defaultItems);

  function newItemTransform(item) {
    return {
      label: item.value,
      value: item.value.toLowerCase(),
    };
  }

  function resetItems() {
    updateItems([...defaultItems]);
  }

  function onItemCreated(item) {
    updateItems([...items, item]);
  }

  return (
    <>
      <button onClick={resetItems}>Reset Items</button>
      <strong>Items: </strong>
      <pre>{JSON.stringify(items, null, 2)}</pre>
      <Combobox
        id="controlledItemsId"
        labelText="Controlled items"
        items={items}
        newItemTransform={newItemTransform}
        onItemCreated={onItemCreated}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Disabled`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      id="disabledSelectId"
      disabled
      labelText="Disabled"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
    />
  );
}
`}</code></pre>
      <h2>{`Clearable`}</h2>
      <p>{`The optional `}<inlineCode parentName="p">{`isClearable`}</inlineCode>{` prop allows the user to clear the field once a selection has been made.`}</p>
      <p>{`When using the `}<inlineCode parentName="p">{`isClearable`}</inlineCode>{` prop you can choose a default selected item to be set once the `}<inlineCode parentName="p">{`combobox`}</inlineCode>{` is cleared
using the `}<inlineCode parentName="p">{`defaultSelectedItem`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Combobox
        id="clearableSelectId"
        labelText="Clearable"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialSelectedItem={{ label: 'Green', value: 'green' }}
        isClearable
      />

      <Combobox
        id="clearableWithDefaultsId"
        labelText="Clearable with defaults"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        isClearable
        defaultSelectedItem={{ label: 'Blue', value: 'blue' }}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Error Message`}</h2>
      <p>{`If a select has an `}<inlineCode parentName="p">{`errorMessage`}</inlineCode>{`, the select will be styled to highlight it's error state and the error message will appear below the field.
If an error message is present, it will replace the helper text. Can be a node or a string.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      id="errorMessage"
      labelText="Error message"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      errorMessage="Please select a color"
    />
  );
}
`}</code></pre>
      <h2>{`Helper Message`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`helperMessage`}</inlineCode>{` appears underneath the select field. It will not appear if an error message is present. Can be a node or a string.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      id="helperMessage"
      labelText="Helper message"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      helperMessage="Helper text goes here"
    />
  );
}
`}</code></pre>
      <h2>{`Placeholder`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`placeholder`}</inlineCode>{` text of an input can be set using the `}<inlineCode parentName="p">{`placeholder`}</inlineCode>{` prop.
Placeholder text should be used to provide supplemental information about the input field. It should not be
relied upon to take the place of the label text.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      labelText="Placeholder text"
      items={['Red', 'Blue', 'Green']}
      placeholder="Enter or select a color"
    />
  );
}
`}</code></pre>
      <h2>{`Accessibility`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`getA11yStatusMessage`}</inlineCode>{` prop is a passed in function that allows for customization of the screen-reader accessible message whenever the following props
are changed: `}<inlineCode parentName="p">{`items`}</inlineCode>{`, `}<inlineCode parentName="p">{`highlightedIndex`}</inlineCode>{`, `}<inlineCode parentName="p">{`inputValue`}</inlineCode>{` or `}<inlineCode parentName="p">{`isOpen`}</inlineCode>{`.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`getA11ySelectionMessage`}</inlineCode>{` prop is a passed in function that allows for customization of the screen-reader accessible message
whenever an item has been selected.`}</p>
      <p>{`For both the `}<inlineCode parentName="p">{`getA11yStatusMessage`}</inlineCode>{` and `}<inlineCode parentName="p">{`getA11ySelectionMessage`}</inlineCode>{` functions there is an
`}<a parentName="p" {...{
          "href": "https://github.com/downshift-js/downshift/tree/master/src/hooks/useSelect#geta11yselectionmessage"
        }}>{`object passed with internal state data`}</a>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      id="newA11yStatusMessageId"
      labelText="New accessibility status message"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      getA11yStatusMessage={({ highlightedItem }) =>
        \`custom message saying that \${
          highlightedItem ? highlightedItem.label : 'nothing'
        } is highlighted\`
      }
      getA11ySelectionMessage={({ selectedItem }) =>
        \`custom message saying that \${
          selectedItem ? selectedItem.label : 'nothing'
        } is now selected\`
      }
    />
  );
}
`}</code></pre>
      <h2>{`Disable Create Item`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`disableCreateItem`}</inlineCode>{` prop is an optional boolean and can be used to prevent the user from creating their own items.
This is commonly used to provide a typeahead dropdown without allowing custom options.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      disableCreateItem
      id="disableCreateItemId"
      labelText="Disable create item"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
    />
  );
}
`}</code></pre>
      <h2>{`Multi Combobox`}</h2>
      <p>{`Use `}<inlineCode parentName="p">{`hasPersistentMenu`}</inlineCode>{` prop on multi comboboxes to keep the list of items open after a selection.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const [selectedItems, updateSelectedItems] = React.useState([]);

  function handleSelectedItemsChange(changes) {
    updateSelectedItems(changes.selectedItems);
  }

  function handleRemoveSelectedItem(removedItem) {
    updateSelectedItems(selectedItems.filter(item => item !== removedItem));
  }

  return (
    <>
      <Combobox
        isMulti
        hasPersistentMenu
        id="multiComboboxId"
        labelText="Multi combobox"
        initialSelectedItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
        ]}
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
          { label: 'Orange', value: 'orange' },
          { label: 'Aqua', value: 'aqua' },
          { label: 'Gold', value: 'gold' },
          { label: 'Periwinkle', value: 'periwinkle' },
          { label: 'Lavender', value: 'lavender' },
          { label: 'Marigold', value: 'marigold' },
          { label: 'Yellow', value: 'yellow' },
          { label: 'Purple', value: 'purple' },
          { label: 'Dusty Rose', value: 'dusty_rose' },
          { label: 'Burnt Sienna', value: 'burnt_sienna' },
        ]}
      />

      <Combobox
        isMulti
        hasPersistentMenu
        id="multiComboboxId2"
        labelText="Multi combobox"
        initialSelectedItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
          { label: 'Orange', value: 'orange' },
          { label: 'Aqua', value: 'aqua' },
          { label: 'Gold', value: 'gold' },
          { label: 'Periwinkle', value: 'periwinkle' },
          { label: 'Lavender', value: 'lavender' },
          { label: 'Marigold', value: 'marigold' },
          { label: 'Yellow', value: 'yellow' },
          { label: 'Purple', value: 'purple' },
          { label: 'Dusty Rose', value: 'dusty_rose' },
          { label: 'Burnt Sienna', value: 'burnt_sienna' },
        ]}
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
          { label: 'Orange', value: 'orange' },
          { label: 'Aqua', value: 'aqua' },
          { label: 'Gold', value: 'gold' },
          { label: 'Periwinkle', value: 'periwinkle' },
          { label: 'Lavender', value: 'lavender' },
          { label: 'Marigold', value: 'marigold' },
          { label: 'Yellow', value: 'yellow' },
          { label: 'Purple', value: 'purple' },
          { label: 'Dusty Rose', value: 'dusty_rose' },
          { label: 'Burnt Sienna', value: 'burnt_sienna' },
        ]}
      />

      <Combobox
        errorMessage="Please correct this error"
        id="multiComboboxControlledId"
        isMulti
        hasPersistentMenu
        labelText="Multi combobox controlled"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        selectedItems={selectedItems}
        onSelectedItemsChange={handleSelectedItemsChange}
        onRemoveSelectedItem={handleRemoveSelectedItem}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Events`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const [currentComboboxEvent, updateCurrentComboboxEvent] = React.useState<
    string | undefined
  >(undefined);

  function handleComboboxInputBlur(event) {
    updateCurrentComboboxEvent('Blur');
  }

  function handleComboboxInputFocus(event) {
    updateCurrentComboboxEvent('Focus');
  }

  function handleComboboxInputKeyPress(event) {
    updateCurrentComboboxEvent('KeyPress');
  }

  return (
    <>
      <p>
        <strong>{currentComboboxEvent || 'No'} event was triggered</strong>
      </p>

      <Combobox
        id="comboboxFocusEventId"
        labelText="Combobox focus events"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        onInputBlur={handleComboboxInputBlur}
        onInputFocus={handleComboboxInputFocus}
        onInputKeyPress={handleComboboxInputKeyPress}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Async`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const [isLoading, updateIsLoading] = React.useState<boolean>(false);

  function loadItems() {
    return new Promise(resolve =>
      resolve([
        { label: 'Yellow', value: 'yellow' },
        { label: 'Pink', value: 'pink' },
        { label: 'Periwinkle', value: 'periwinkle' },
      ])
    );
  }

  function handleInputValueChange(changes, setInputItems) {
    const { inputValue } = changes;

    if (!inputValue) return;

    updateIsLoading(true);

    setTimeout(() => {
      updateIsLoading(false);
      loadItems().then((items: any) => {
        setInputItems(
          items.filter(item =>
            item.label.toLowerCase().startsWith(inputValue.toLowerCase())
          )
        );
      });
    }, 1000);
  }

  return (
    <Combobox
      id="asyncId"
      labelText="Async"
      isLoading={isLoading}
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      onInputValueChange={handleInputValueChange}
    />
  );
}
`}</code></pre>
      <h2>{`Typeahead`}</h2>
      <p>{`When using Combobox for typeahead with a large items list, use the `}<inlineCode parentName="p">{`isTypeahead`}</inlineCode>{` prop to allow the selected item to not be part of the original `}<inlineCode parentName="p">{`items`}</inlineCode>{` list. In addition, when `}<inlineCode parentName="p">{`isTypeahead`}</inlineCode>{` and `}<inlineCode parentName="p">{`isLoading`}</inlineCode>{` are both true, the loading indicator will appear on the list of items instead of the input.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const dataSet = [
    'Aardvark',
    'Aardwolf',
    'Abyssinian',
    'Addax',
    'Adelie Penguin',
    'Affenpinscher',
    'Afghan Hound',
    'African Bullfrog',
    'African Bush Elephant',
    'African Civet',
    'African Clawed Frog',
    'African Forest Elephant',
    'African Grey Parrot',
    'African Palm Civet',
    'African Penguin',
    'African Tree Toad',
    'African Wild Dog',
    'Aidi',
    'Ainu',
    'Airedale Terrier',
    'Airedoodle',
    'Akbash',
    'Akita',
    'Akita Shepherd',
    'Alabai',
    'Alaskan Husky',
    'Alaskan Klee Kai',
    'Alaskan Malamute',
    'Alaskan Shepherd',
    'Albacore Tuna',
    'Albatross',
    'Aldabra Giant Tortoise',
    'Alligator',
    'Alligator Gar',
    'Alpaca',
    'Alpine Dachsbracke',
    'Alpine Goat',
    'Alusky',
    'Amazon Parrot',
    'Amazon River Dolphin (Pink Dolphin)',
    'Ambrosia Beetle',
    'American Alsatian',
    'American Bulldog',
    'American Cocker Spaniel',
    'American Cockroach',
    'American Coonhound',
    'American Eskimo Dog',
    'American Foxhound',
    'American Hairless Terrier',
    'American Leopard Hound',
    'American Pit Bull Terrier',
    'American Pygmy Goat',
    'American Robin',
    'American Staffordshire Terrier',
    'American Toad',
    'American Water Spaniel',
    'Amur Leopard',
    'Anatolian Shepherd Dog',
    'Anchovies',
    'Angelfish',
    'Anglerfish',
    'Angora Goat',
    'Ant',
    'Antarctic scale worm',
    'Anteater',
    'Antelope',
    'Appenzeller Dog',
    'Apple Head Chihuahua',
    'Arapaima',
    'Arctic Fox',
    'Arctic Hare',
    'Arctic Wolf',
    'Arizona Bark Scorpion',
    'Armadillo',
    'Armyworm',
    'Asian Elephant',
    'Asian Giant Hornet',
    'Asian Palm Civet',
    'Asiatic Black Bear',
    'Aurochs',
    'Aussiedoodle',
    'Aussiedor',
    'Australian Bulldog',
    'Australian Cattle Dog',
    'Australian Gecko',
    'Australian Kelpie Dog',
    'Australian Labradoodle',
    'Australian Mist',
    'Australian Retriever',
    'Australian Shepherd',
    'Australian Terrier',
    'Avocet',
    'Axolotl',
  ];

  const [suggestedItems, setSuggestedItems] = React.useState([]);
  const [, updateSelectedItems] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [inputQuery, setInputQuery] = React.useState('');

  function handleSelectedItemsChange(changes) {
    updateSelectedItems(changes.selectedItems);
  }

  function onInputKeyPress(event) {
    setInputQuery(event.target.value + event.key);
  }

  function onInputValueChange(event) {
    setInputQuery(event.inputValue);
  }

  React.useEffect(() => {
    setTimeout(() => {
      setIsLoading(true);
    }, 100);

    setTimeout(() => {
      const matches = dataSet.filter(item => {
        return item.toLowerCase().includes(inputQuery.toLowerCase());
      });
      const newSuggestedItems = matches.slice(0, 5).map(item => {
        return { label: item, value: item };
      });

      setTimeout(() => {
        setSuggestedItems(newSuggestedItems);
        setIsLoading(false);
      }, 100);
    }, 1000);
  }, [inputQuery]);

  return (
    <Combobox
      isTypeahead
      isClearable
      isMulti
      disableCreateItem
      items={suggestedItems}
      isLoading={isLoading}
      labelText="Typeahead Example - Animals"
      onInputKeyPress={onInputKeyPress}
      onInputValueChange={onInputValueChange}
      onSelectedItemsChange={handleSelectedItemsChange}
    />
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <p>{`The isInverse prop is an optional boolean used when the component is to be displayed on a dark background.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Card, CardBody, Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <Combobox
          id="comboboxInverseId"
          isInverse
          labelText="Combobox"
          defaultItems={[
            { label: 'Red', value: 'red' },
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' },
          ]}
        />

        <Combobox
          errorMessage="Please correct this error"
          isMulti
          id="comboboxMultiInverseId"
          isInverse
          labelText="Multi combobox"
          defaultItems={[
            { label: 'Red', value: 'red' },
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' },
          ]}
          initialSelectedItems={[
            { label: 'Red', value: 'red' },
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' },
          ]}
        />
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Custom Items List Height`}</h2>
      <p>{`The items list menu has a default max height that can be changed in the theme or using the `}<inlineCode parentName="p">{`itemListMaxHeight`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      labelText="Combobox small item list menu"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      itemListMaxHeight="50px"
    />
  );
}
`}</code></pre>
      <h2>{`Label Position`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`labelPosition`}</inlineCode>{` prop can be used to set the position of the text label relative to the form field. It accepts either `}<inlineCode parentName="p">{`top`}</inlineCode>{` or `}<inlineCode parentName="p">{`left`}</inlineCode>{`, with `}<inlineCode parentName="p">{`top`}</inlineCode>{` as the default.
Left-aligned lables are not recommended for use in standard forms; instead they are designed for smaller spaces where vertical space is limited, such as in a toolbar.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox, LabelPosition } from 'react-magma-dom';

export function Example() {
  return (
    <Combobox
      labelPosition={LabelPosition.left}
      labelText="Left-aligned label"
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
    />
  );
}
`}</code></pre>
      <h2>{`Custom Styles`}</h2>
      <p>{`Custom styles can be passed into the Combobox component. The `}<inlineCode parentName="p">{`containerStyle`}</inlineCode>{` property will apply to the container.
Additional `}<inlineCode parentName="p">{`labelStyle`}</inlineCode>{`, `}<inlineCode parentName="p">{`inputStyle`}</inlineCode>{` and `}<inlineCode parentName="p">{`messageStyle`}</inlineCode>{` properties are available to style the respective elements.
Please use discretion when adding custom styles.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const [selectedItem, updateSelectedItem] = React.useState('');

  function onSelectedItemChange(changes) {
    updateSelectedItem(changes.selectedItem);
  }

  return (
    <>
      <Combobox
        containerStyle={{ marginBottom: '32px' }}
        helperMessage="Helper message"
        inputStyle={{ border: '2px dotted green', width: '200px' }}
        items={['Red', 'Blue', 'Green']}
        labelStyle={{ fontStyle: 'italic' }}
        labelText="Basic with custom styles"
        messageStyle={{ border: '1px solid blue' }}
        selectedItem={selectedItem}
        onSelectedItemChange={onSelectedItemChange}
      />

      <Combobox
        containerStyle={{ marginBottom: '32px' }}
        helperMessage="Helper message"
        inputStyle={{ border: '2px dotted green', width: '200px' }}
        isMulti
        items={['Red', 'Blue', 'Green']}
        labelStyle={{ fontStyle: 'italic' }}
        labelText="Multi with custom styles"
        messageStyle={{ border: '1px solid blue' }}
        selectedItem={selectedItem}
        onSelectedItemChange={onSelectedItemChange}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Items`}</h2>
      <p>{`By default each component accepts an array of `}<inlineCode parentName="p">{`items`}</inlineCode>{` with either a `}<inlineCode parentName="p">{`string`}</inlineCode>{` or an `}<inlineCode parentName="p">{`object`}</inlineCode>{` with the shape `}<inlineCode parentName="p">{`{item: string; label: string;}`}</inlineCode>{`
for each `}<inlineCode parentName="p">{`item`}</inlineCode>{`. If you need to pass in a custom shape for your `}<inlineCode parentName="p">{`items`}</inlineCode>{` you can pass in an additional prop named `}<inlineCode parentName="p">{`itemToString`}</inlineCode>{`
which is a function that returns the string representation of your `}<inlineCode parentName="p">{`item`}</inlineCode>{` which will be applied as the label.`}</p>
      <Alert variant="warning" mdxType="Alert">
  <strong>WARNING</strong> Your function must include a{' '}
  <inlineCode>null</inlineCode> check.
      </Alert>
      <p>{`If you are using a custom shape for your `}<inlineCode parentName="p">{`items`}</inlineCode>{` in a `}<inlineCode parentName="p">{`typescript`}</inlineCode>{` project refer to our example of `}<a parentName="p" {...{
          "href": "/version/4.9.1/api/combobox#custom_items_typescript"
        }}>{`using your
custom `}<inlineCode parentName="a">{`type`}</inlineCode></a>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';
import { v4 as uuidv4 } from 'uuid';

export function Example() {
  const [controlledSelectedItem, updateControlledSelectedItem] =
    React.useState('');
  const [controlledItems, updateControlledItems] = React.useState([
    { id: 0, name: 'red', representation: 'Red', hex: '#FF0000' },
    { id: 1, name: 'blue', representation: 'Blue', hex: '#0000FF' },
    { id: 2, name: 'green', representation: 'Green', hex: '#008000' },
  ]);

  function itemToString(item): string {
    return item
      ? \`\${item.representation}\${item.hex ? \` (\${item.hex})\` : ''}\`
      : '';
  }

  function newItemTransform(item) {
    const { value } = item;

    return {
      id: uuidv4(),
      name: value,
      hex: item.hex,
      representation: value.charAt(0).toUpperCase() + value.slice(1),
    };
  }

  function handleSelectedItemChange(changes) {
    updateControlledSelectedItem(changes.selectedItem);
  }

  function handleItemCreated(newItem) {
    updateControlledSelectedItem(newItem);
    updateControlledItems(oldItems => [...oldItems, newItem]);
  }

  return (
    <>
      <Combobox
        labelText="Combobox with custom items"
        defaultItems={[
          { id: 10, name: 'red', representation: 'Red', hex: '#FF0000' },
          { id: 11, name: 'blue', representation: 'Blue', hex: '#0000FF' },
          { id: 12, name: 'green', representation: 'Green', hex: '#008000' },
        ]}
        newItemTransform={newItemTransform}
        itemToString={itemToString}
      />

      {controlledSelectedItem && (
        <>
          <p>
            <strong>Controlled Selected Item</strong>
          </p>
          <pre>{JSON.stringify(controlledSelectedItem, null, 2)}</pre>
        </>
      )}

      <Combobox
        labelText="Controlled combobox with custom items"
        items={controlledItems}
        newItemTransform={newItemTransform}
        itemToString={itemToString}
        onItemCreated={handleItemCreated}
        onSelectedItemChange={handleSelectedItemChange}
        selectedItem={controlledSelectedItem}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Items Typescript`}</h2>
      <p>{`When using a custom shape for your `}<inlineCode parentName="p">{`items`}</inlineCode>{` in a `}<inlineCode parentName="p">{`typescript`}</inlineCode>{` project you will need to provide the `}<inlineCode parentName="p">{`Combobox`}</inlineCode>{`
element with the shape of the `}<inlineCode parentName="p">{`items`}</inlineCode>{` you are passing in.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';
import { v4 as uuidv4 } from 'uuid';

interface CustomComboboxItem {
  id: number;
  actual: string;
  representation: string;
  hex?: string;
}

export function Example() {
  const customItems: CustomComboboxItem[] = [
    {
      id: 21,
      actual: 'red',
      representation: 'Red',
      hex: '#FF0000',
    },
    {
      id: 22,
      actual: 'blue',
      representation: 'Blue',
      hex: '#0000FF',
    },
    {
      id: 23,
      actual: 'green',
      representation: 'Green',
      hex: '#008000',
    },
  ];

  function itemToString(item: CustomComboboxItem): string {
    return item ? \`\${item.representation} (\${item.hex})\` : '';
  }

  function newItemTransform(item: {
    label: string;
    value: string;
  }): CustomComboboxItem {
    const { value } = item;

    return {
      id: uuidv4(),
      actual: value,
      representation: value.charAt(0).toUpperCase() + value.slice(1),
    };
  }

  return (
    <>
      <Combobox<CustomComboboxItem>
        id="customItemToStringTypescriptId"
        labelText="Custom items with typescript"
        defaultItems={customItems}
        itemToString={itemToString}
        newItemTransform={newItemTransform}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Components`}</h2>
      <p>{`Out of the box, the `}<inlineCode parentName="p">{`Combobox`}</inlineCode>{` renders a styled `}<inlineCode parentName="p">{`li`}</inlineCode>{` for items and uses React Magma-styled iconography for the button used to clear the selection, the caret for the dropdown, and the loading animation.
However, these can be overridden by providing custom components to the `}<inlineCode parentName="p">{`components`}</inlineCode>{` prop, in an object containing an `}<inlineCode parentName="p">{`Item`}</inlineCode>{`, a `}<inlineCode parentName="p">{`ClearIndicator`}</inlineCode>{`, a `}<inlineCode parentName="p">{`DropdownIndicator`}</inlineCode>{` and a `}<inlineCode parentName="p">{`LoadingIndicator`}</inlineCode>{`.`}</p>
      <h3>{`Custom Item Component`}</h3>
      <p>{`By default each item will be rendered with the `}<inlineCode parentName="p">{`itemToString`}</inlineCode>{` value in a styled `}<inlineCode parentName="p">{`li`}</inlineCode>{`. If you would like to add more information or
would just like to be able to fully customize the look of your items you can pass in an `}<inlineCode parentName="p">{`Item`}</inlineCode>{` component that uses the `}<inlineCode parentName="p">{`props`}</inlineCode>{` that
we use internally.`}</p>
      <Alert variant="warning" mdxType="Alert">
  Be aware. You must pass a <inlineCode>ref</inlineCode> to your custom item
  component to make sure the functionality of the entire{' '}
  <inlineCode>Combobox</inlineCode> continues to work.
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import styled from '@emotion/styled';
import { Combobox } from 'react-magma-dom';

export function Example() {
  const CustomStyledItem = styled.li(props => ({
    alignSelf: 'center',
    background: props.isFocused ? props.theme.colors.neutral200 : 'transparent',
    lineHeight: '24px',
    margin: '0',
    padding: '8px 16px',
  }));

  const ContainerSpan = styled.span(props => ({
    display: 'flex',
    alignItems: 'center',
  }));

  const Hex = styled.span(props => ({
    background: props.color,
    border: \`2px solid \${props.theme.colors.neutral}\`,
    borderRadius: props.theme.borderRadius,
    display: 'inline-flex',
    height: '16px',
    marginRight: '4px',
    width: '16px',
  }));

  const Description = styled.div(props => ({
    fontSize: '12px',
    color: props.theme.colors.neutral500,
  }));

  function itemToString(item) {
    return item ? item.representation : '';
  }

  const CustomItem = props => {
    const { itemRef, itemString, item, ...other } = props;
    return (
      <CustomStyledItem {...other} ref={itemRef}>
        <ContainerSpan>
          <Hex color={item.hex} theme={props.theme} />
          {itemString}
        </ContainerSpan>
        <Description theme={props.theme}>{item.description}</Description>
      </CustomStyledItem>
    );
  };

  return (
    <Combobox
      components={{ Item: CustomItem }}
      defaultItems={[
        {
          id: 30,
          name: 'red',
          representation: 'Red',
          hex: '#FF0000',
          description: 'The color of roses',
        },
        {
          id: 31,
          name: 'blue',
          representation: 'Blue',
          hex: '#0000FF',
          description: 'The color of blueberries',
        },
        {
          id: 32,
          name: 'green',
          representation: 'Green',
          hex: '#008000',
          description: 'The color of grass',
        },
      ]}
      disableCreateItem
      itemToString={itemToString}
      labelText="Combobox with custom item render"
    />
  );
}
`}</code></pre>
      <h3>{`Other Custom Components`}</h3>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox } from 'react-magma-dom';

export function Example() {
  const [isLoading, updateIsLoading] = React.useState<boolean>(false);

  function loadItems() {
    return new Promise(resolve =>
      resolve([
        { label: 'Yellow', value: 'yellow' },
        { label: 'Pink', value: 'pink' },
        { label: 'Periwinkle', value: 'periwinkle' },
      ])
    );
  }

  function handleInputValueChange(changes, setInputItems) {
    const { inputValue } = changes;

    if (!inputValue) return;

    updateIsLoading(true);

    setTimeout(() => {
      updateIsLoading(false);
      loadItems().then((items: any) => {
        setInputItems(
          items.filter(item =>
            item.label.toLowerCase().startsWith(inputValue.toLowerCase())
          )
        );
      });
    }, 1000);
  }

  return (
    <Combobox
      id="customComponentsId"
      labelText="Custom components"
      isLoading={isLoading}
      defaultItems={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      isClearable
      onInputValueChange={handleInputValueChange}
      components={{
        ClearIndicator: props => <button onClick={props.onClick}>Clear</button>,
        DropdownIndicator: React.forwardRef((props, ref) => (
          <button ref={ref} onClick={props.onClick}>
            Dropdown
          </button>
        )),
        LoadingIndicator: props => <>Loading</>,
      }}
    />
  );
}
`}</code></pre>
      <h2>{`Internationalization`}</h2>
      <p>{`Some of the internationalization overrides use placeholders to insert selected values in to the message.
Placeholders are specific keywords surrounded by curly braces.`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`{labelText}`}</inlineCode>{` will be replaced with the comboboxes `}<inlineCode parentName="li">{`labelText`}</inlineCode>{`.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`{selectedItem}`}</inlineCode>{` will be replaced by the current `}<inlineCode parentName="li">{`itemToString`}</inlineCode>{` representation of the `}<inlineCode parentName="li">{`selectedItem`}</inlineCode>{` of the `}<inlineCode parentName="li">{`combobox`}</inlineCode>{`.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`{inputValue}`}</inlineCode>{` will be replaced by the current `}<inlineCode parentName="li">{`inputValue`}</inlineCode>{` of the `}<inlineCode parentName="li">{`combobox`}</inlineCode>{` input.`}</li>
      </ul>
      <p>{`Full example of `}<Link to="/api/internationalization" mdxType="Link">{`internationalization override options`}</Link></p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Combobox, I18nContext, defaultI18n } from 'react-magma-dom';

export function Example() {
  return (
    <I18nContext.Provider
      value={{
        ...defaultI18n,
        combobox: {
          clearIndicatorAriaLabel:
            'click to reset selection for {labelText}. {selectedItem} is currently selected',
          createLabel: 'Custom Create "{inputValue}"',
        },
        multiCombobox: {
          selectedItemButtonAriaLabel:
            'click to reset the selected item {selectedItem}',
        },
      }}
    >
      <Combobox
        labelText="Internationalization"
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        isClearable
        initialSelectedItem={{ label: 'Red', value: 'red' }}
      />

      <Combobox
        labelText="Multi internationalization"
        isMulti
        defaultItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialSelectedItems={[{ label: 'Red', value: 'red' }]}
      />
    </I18nContext.Provider>
  );
}
`}</code></pre>
      <h2>{`State Change Types`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`type`}</inlineCode>{` property in the `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object that is returned from the `}<inlineCode parentName="p">{`onStateChange`}</inlineCode>{` function
corresponds to a `}<inlineCode parentName="p">{`stateChangeTypes`}</inlineCode>{` property. The list of all the possible types for a `}<inlineCode parentName="p">{`combobox`}</inlineCode>{` or a
`}<inlineCode parentName="p">{`multi-combobox`}</inlineCode>{` are listed below.`}</p>
      <Alert variant="info" style={{
        maxWidth: '100%',
        overflow: 'scroll'
      }} mdxType="Alert">
  In the development environment these types equate to strings
  <br />
  (eg:{' '}
  <inlineCode className="inverse">
    ComboboxStateChangeTypes.InputKeyDownArrowDown =
    '__input_keydown_arrow_down__'
  </inlineCode>
  ). However, in the production environment the types equate to numbers
  <br />
  (eg:
  <inlineCode className="inverse">
    ComboboxStateChangeTypes.InputKeyDownArrowDown = 0
  </inlineCode>
  ).
      </Alert>
      <h3>{`Combobox`}</h3>
      <div style={{
        overflowX: 'scroll'
      }}>
  <ul>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputKeyDownArrowDown</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputKeyDownArrowUp</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputKeyDownEscape</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputKeyDownHome</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputKeyDownEnd</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputKeyDownEnter</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputChange</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.InputBlur</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.MenuMouseLeave</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.ItemMouseMove</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.ItemClick</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.ToggleButtonClick</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.FunctionToggleMenu</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.FunctionOpenMenu</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.FunctionCloseMenu</inlineCode>
    </li>
    <li>
      <inlineCode>
        ComboboxStateChangeTypes.FunctionSetHighlightedIndex
      </inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.FunctionSelectItem</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.FunctionSetInputValue</inlineCode>
    </li>
    <li>
      <inlineCode>ComboboxStateChangeTypes.FunctionReset</inlineCode>
    </li>
  </ul>
      </div>
      <h3>{`MultiCombobox`}</h3>
      <div style={{
        overflowX: 'scroll'
      }}>
  <ul>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemClick
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownDelete
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownBackspace
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownNavigationNext
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownNavigationPrevious
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.DropdownKeyDownNavigationPrevious
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.DropdownKeyDownBackspace
      </inlineCode>
    </li>
    <li>
      <inlineCode>MultipleSelectionStateChangeTypes.DropdownClick</inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionAddSelectedItem
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionRemoveSelectedItem
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionSetSelectedItems
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionSetActiveIndex
      </inlineCode>
    </li>
    <li>
      <inlineCode>MultipleSelectionStateChangeTypes.FunctionReset</inlineCode>
    </li>
  </ul>
      </div>
      <h2>{`Combobox Props`}</h2>
      <ComboboxProps mdxType="ComboboxProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      